import { useAdminData } from './hooks/useAdminData';
import { useAdminModal } from './hooks/useAdminModal';
import { Modal, Button } from '../../../components/ui';
import AdminTabs from './components/AdminTabs';
import PartnersTable from './components/PartnersTable';
import ClientsTable from './components/ClientsTable';
import AwsUsersTable from './components/AwsUsersTable';
import ProjectsTable from './components/ProjectsTable';
import UsersTable from './components/UsersTable';
import ErrorDisplay from '../../../components/ErrorDisplay';

// Import form components
import { PartnerForm, ClientForm, AwsUserForm, ProjectForm, UserForm } from '../../../components/AdminForms';

const AdminPage = () => {
  // Custom hooks
  const {
    activeTab,
    setActiveTab,
    partners,
    clients,
    awsUsers,
    projects,
    users,
    loading,
    error,
    loadData,
    handleDelete,
    handleSave
  } = useAdminData();

  const {
    showModal,
    selectedItem,
    openModal,
    closeModal,
    handleEdit,
  } = useAdminModal();

  const renderTable = () => {
    if (loading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      );
    }

    if (error) {
      return (
        <ErrorDisplay
          error={error}
          title="Error Loading Data"
          onRetry={loadData}
        />
      );
    }

    switch (activeTab) {
      case 'partners':
        return (
          <PartnersTable
            partners={partners}
            onEdit={(partner) => {
              setActiveTab('partners');
              handleEdit(partner);
            }}
            onDelete={handleDelete}
          />
        );
      case 'clients':
        return (
          <ClientsTable
            clients={clients}
            onEdit={(client) => {
              setActiveTab('clients');
              handleEdit(client);
            }}
            onDelete={handleDelete}
          />
        );
      case 'aws-users':
        return (
          <AwsUsersTable
            awsUsers={awsUsers}
            onEdit={(awsUser) => {
              setActiveTab('aws-users');
              handleEdit(awsUser);
            }}
            onDelete={handleDelete}
          />
        );
      case 'projects':
        return (
          <ProjectsTable
            projects={projects}
            onEdit={(project) => {
              setActiveTab('projects');
              handleEdit(project);
            }}
            onDelete={handleDelete}
          />
        );
      case 'users':
        return (
          <UsersTable
            users={users}
            onEdit={(user) => {
              setActiveTab('users');
              handleEdit(user);
            }}
            onDelete={handleDelete}
          />
        );
      default:
        return null;
    }
  };

  const renderForm = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSave = handleSave as (data: any) => Promise<{ success: boolean } | null>;
    const props = {
      onSave,
      onCancel: closeModal
    };

    switch (activeTab) {
      case 'partners':
        return <PartnerForm partner={selectedItem} {...props} />;
      case 'clients':
        return <ClientForm client={selectedItem} {...props} />;
      case 'aws-users':
        return <AwsUserForm awsUser={selectedItem} {...props} />;
      case 'projects':
        return <ProjectForm project={selectedItem} {...props} />;
      case 'users':
        return <UserForm user={selectedItem} {...props} />;
      default:
        return null;
    }
  };

  const handleCreateNew = () => {
    openModal(activeTab, null);
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            Admin Management
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
            Manage partners, clients, users, and projects
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          Add New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' ')}
        </Button>
      </div>

      {/* Tabs */}
      <AdminTabs activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Content */}
      <div className="mt-6">
        {renderTable()}
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={`${selectedItem ? 'Edit' : 'Create'} ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' ')}`}
        maxWidth="max-w-2xl"
      >
        {renderForm()}
      </Modal>
    </div>
  );
};

export default AdminPage;
